import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SellerOnboardingAlert from "../components/SellerOnboardingAlert";
import {
    CONTACT_HIERARCHY_LEVELS,
    CONTACT_ORG_TYPE_OPTIONS,
    CONTACT_VERTICAL_OPTIONS,
} from "../features/contacts/constants";
import { contactsService } from "../features/contacts/service";
import type { BusinessContactCreatePayload } from "../features/contacts/types";
import type { ParsedApiError } from "../lib/http/apiErrors";
import { parseApiError } from "../lib/http/apiErrors";

function splitCommaList(value: string): string[] {
    return value.split(",").map((s) => s.trim()).filter(Boolean);
}

const ContactPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [contactName, setContactName] = useState("");
    const [contactDesignation, setContactDesignation] = useState("");
    const [contactPhone, setContactPhone] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [contactVertical, setContactVertical] = useState("");
    const [contactCompany, setContactCompany] = useState("");
    const [contactCompanyCode, setContactCompanyCode] = useState("");
    const [contactOrgType, setContactOrgType] = useState("");
    const [contactDepartment, setContactDepartment] = useState("");
    const [contactSubDepartment, setContactSubDepartment] = useState("");
    const [contactHierarchyLevel, setContactHierarchyLevel] = useState("");
    const [contactRegion, setContactRegion] = useState("");
    const [contactCoverage, setContactCoverage] = useState("");
    const [contactCity, setContactCity] = useState("");
    const [contactCountry, setContactCountry] = useState("");
    const [contactSpecialties, setContactSpecialties] = useState("");
    const [contactNotes, setContactNotes] = useState("");
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");

    const [saving, setSaving] = useState(false);
    const [savedContactId, setSavedContactId] = useState<string | null>(null);
    const [errorMsg, setErrorMsg] = useState("");
    const [errorInfo, setErrorInfo] = useState<ParsedApiError | null>(null);

    const resetForm = () => {
        setContactName("");
        setContactDesignation("");
        setContactPhone("");
        setContactEmail("");
        setContactVertical("");
        setContactCompany("");
        setContactCompanyCode("");
        setContactOrgType("");
        setContactDepartment("");
        setContactSubDepartment("");
        setContactHierarchyLevel("");
        setContactRegion("");
        setContactCoverage("");
        setContactCity("");
        setContactCountry("");
        setContactSpecialties("");
        setContactNotes("");
        setTitle("");
        setDescription("");
        setSavedContactId(null);
        setErrorMsg("");
        setErrorInfo(null);
    };

    const validate = (): boolean => {
        if (!contactName.trim()) {
            setErrorMsg("Please enter full name.");
            return false;
        }
        if (!contactVertical.trim()) {
            setErrorMsg("Please select a vertical.");
            return false;
        }
        if (!contactCompany.trim()) {
            setErrorMsg("Please enter company / organization.");
            return false;
        }
        if (!contactOrgType.trim()) {
            setErrorMsg("Please select organization type.");
            return false;
        }
        if (!title.trim()) {
            setErrorMsg("Please enter a card title.");
            return false;
        }
        if (!description.trim()) {
            setErrorMsg("Please enter a description.");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorMsg("");
        setErrorInfo(null);
        if (!validate()) return;

        setSaving(true);
        try {
            const phone = contactPhone.trim();
            const email = contactEmail.trim();
            const desig = contactDesignation.trim();
            const levelRaw = contactHierarchyLevel.trim();
            const parsedLevel = levelRaw === "" ? NaN : parseInt(levelRaw, 10);

            const payload: BusinessContactCreatePayload = {
                title: title.trim(),
                description: description.trim(),
                contact_name: contactName.trim(),
                designation: desig || undefined,
                roles: desig ? [desig] : [],
                phone_numbers: phone ? [phone] : [],
                whatsapp_numbers: phone ? [phone] : [],
                emails: email ? [email] : [],
                organization_name: contactCompany.trim(),
                organization_type: contactOrgType.trim(),
                department: contactDepartment.trim() || undefined,
                sub_department: contactSubDepartment.trim() || undefined,
                vertical: contactVertical.trim(),
                company_code: contactCompanyCode.trim() || undefined,
                region: contactRegion.trim() || undefined,
                coverage_areas: splitCommaList(contactCoverage),
                hierarchy_level: levelRaw && !Number.isNaN(parsedLevel) ? parsedLevel : undefined,
                city: contactCity.trim() || undefined,
                country: contactCountry.trim() || undefined,
                specialties: splitCommaList(contactSpecialties),
                notes: contactNotes.trim() || undefined,
                category: "Contact",
                category_group: "Contacts",
                item_type: "CONTACT",
                tags: [],
                destinations: [],
                languages: [],
                is_primary: false,
                is_active: true,
            };

            const created = await contactsService.createContact(payload);
            setSavedContactId(created.id);
        } catch (err) {
            const parsed = parseApiError(err, "Failed to save contact. Please try again.");
            setErrorMsg(parsed.message);
            setErrorInfo(parsed);
        } finally {
            setSaving(false);
        }
    };

    if (savedContactId) {
        return (
            <div className="page-shell flex items-center justify-center p-6">
                <div className="bg-white rounded-2xl shadow-lg p-8 max-w-lg w-full">
                    <div className="text-center mb-6">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <span className="text-3xl">👤</span>
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">Contact card saved</h2>
                        <p className="text-sm text-gray-500 mt-1 font-medium">{title.trim() || contactName}</p>
                        <p className="text-xs text-gray-400 mt-2">Business contact ID: {savedContactId}</p>
                    </div>
                    <div className="flex flex-col gap-3">
                        <button
                            type="button"
                            onClick={() => navigate("/inventory/my")}
                            className="w-full py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            My Inventory
                        </button>
                        <button
                            type="button"
                            onClick={resetForm}
                            className="w-full py-3 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 transition-colors shadow-sm"
                        >
                            + Post another contact
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-shell">
            <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
                <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-4">
                    <button
                        type="button"
                        onClick={() => navigate("/deals/create")}
                        className="text-gray-500 hover:text-gray-800 text-sm font-medium"
                    >
                        ← New Listing
                    </button>
                    <div className="h-5 w-px bg-gray-300" />
                    <h1 className="text-xl font-bold text-gray-900">Contact Card</h1>
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-4 py-8">
                <p className="text-sm text-gray-500 mb-6">Create a seller contact card and specialty profile.</p>

                <form onSubmit={handleSubmit} className="space-y-8">
                    <section className="space-y-3">
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Person details</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <label className="block">
                                <span className="block text-sm font-semibold text-gray-800 mb-1">Full name *</span>
                                <input required type="text" value={contactName} onChange={(e) => setContactName(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white" />
                            </label>
                            <label className="block">
                                <span className="block text-sm font-semibold text-gray-800 mb-1">Designation</span>
                                <input type="text" value={contactDesignation} onChange={(e) => setContactDesignation(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white" />
                            </label>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <label className="block">
                                <span className="block text-sm font-semibold text-gray-800 mb-1">Phone / WhatsApp</span>
                                <input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white" />
                            </label>
                            <label className="block">
                                <span className="block text-sm font-semibold text-gray-800 mb-1">Email</span>
                                <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white" />
                            </label>
                        </div>
                    </section>

                    <section className="space-y-3">
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Organization</p>
                        <label className="block">
                            <span className="block text-sm font-semibold text-gray-800 mb-1">Vertical *</span>
                            <select required value={contactVertical} onChange={(e) => setContactVertical(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white">
                                <option value="">Select…</option>
                                {CONTACT_VERTICAL_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                            </select>
                        </label>
                        <label className="block">
                            <span className="block text-sm font-semibold text-gray-800 mb-1">Company / organization *</span>
                            <input required type="text" value={contactCompany} onChange={(e) => setContactCompany(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white" />
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <label className="block">
                                <span className="block text-sm font-semibold text-gray-800 mb-1">Company code</span>
                                <input type="text" value={contactCompanyCode} onChange={(e) => setContactCompanyCode(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white" />
                            </label>
                            <label className="block">
                                <span className="block text-sm font-semibold text-gray-800 mb-1">Organization type *</span>
                                <select required value={contactOrgType} onChange={(e) => setContactOrgType(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white">
                                    <option value="">Select…</option>
                                    {CONTACT_ORG_TYPE_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                                </select>
                            </label>
                        </div>
                    </section>

                    <section className="space-y-3">
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Department &amp; hierarchy</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <label className="block">
                                <span className="block text-sm font-semibold text-gray-800 mb-1">Department</span>
                                <input type="text" value={contactDepartment} onChange={(e) => setContactDepartment(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white" />
                            </label>
                            <label className="block">
                                <span className="block text-sm font-semibold text-gray-800 mb-1">Sub-department</span>
                                <input type="text" value={contactSubDepartment} onChange={(e) => setContactSubDepartment(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white" />
                            </label>
                        </div>
                        <label className="block">
                            <span className="block text-sm font-semibold text-gray-800 mb-1">Hierarchy level</span>
                            <select value={contactHierarchyLevel} onChange={(e) => setContactHierarchyLevel(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white">
                                <option value="">Select…</option>
                                {Object.entries(CONTACT_HIERARCHY_LEVELS).map(([level, label]) => (
                                    <option key={level} value={level}>{`${level}: ${label}`}</option>
                                ))}
                            </select>
                        </label>
                    </section>

                    <section className="space-y-3">
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Region &amp; coverage</p>
                        <label className="block">
                            <span className="block text-sm font-semibold text-gray-800 mb-1">Region</span>
                            <input type="text" value={contactRegion} onChange={(e) => setContactRegion(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white" />
                        </label>
                        <label className="block">
                            <span className="block text-sm font-semibold text-gray-800 mb-1">Coverage areas (comma separated)</span>
                            <input type="text" value={contactCoverage} onChange={(e) => setContactCoverage(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white" />
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <label className="block">
                                <span className="block text-sm font-semibold text-gray-800 mb-1">City</span>
                                <input type="text" value={contactCity} onChange={(e) => setContactCity(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white" />
                            </label>
                            <label className="block">
                                <span className="block text-sm font-semibold text-gray-800 mb-1">Country</span>
                                <input type="text" value={contactCountry} onChange={(e) => setContactCountry(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white" />
                            </label>
                        </div>
                    </section>

                    <section className="space-y-3">
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Specialties &amp; notes</p>
                        <label className="block">
                            <span className="block text-sm font-semibold text-gray-800 mb-1">Specialties (comma separated)</span>
                            <input type="text" value={contactSpecialties} onChange={(e) => setContactSpecialties(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white" />
                        </label>
                        <label className="block">
                            <span className="block text-sm font-semibold text-gray-800 mb-1">Notes</span>
                            <textarea rows={2} value={contactNotes} onChange={(e) => setContactNotes(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white resize-none" />
                        </label>
                        <label className="block">
                            <span className="block text-sm font-semibold text-gray-800 mb-1">Title *</span>
                            <input required type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white" />
                        </label>
                        <label className="block">
                            <span className="block text-sm font-semibold text-gray-800 mb-1">Description *</span>
                            <textarea required rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm bg-white resize-none" />
                        </label>
                    </section>

                    <div className="flex gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                        <span className="text-xl shrink-0" aria-hidden>✨</span>
                        <div>
                            <p className="text-sm font-semibold text-green-900">AI will extract the rest</p>
                            <p className="text-xs text-green-800/90 mt-1 leading-snug">
                                Category, destinations, tags, and richer seller copy can be refined from your profile and listings over time.
                            </p>
                        </div>
                    </div>

                    {errorMsg && (
                        errorInfo?.isSellerOnboardingIncomplete ? (
                            <SellerOnboardingAlert
                                message={errorInfo.message}
                                missingFields={errorInfo.missingFields}
                                missingFieldsDetailed={errorInfo.missingFieldsDetailed}
                                returnTo={location.pathname}
                            />
                        ) : (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{errorMsg}</div>
                        )
                    )}

                    <button
                        type="submit"
                        disabled={saving}
                        className="w-full py-3.5 rounded-xl text-sm font-bold uppercase tracking-wide bg-[#f5f0e6] text-gray-900 border border-[#e8e0d4] hover:bg-[#ebe4d6] transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {saving ? "Saving..." : "Post contact card"}
                    </button>
                </form>
            </main>
        </div>
    );
};

export default ContactPage;
